import type { FailureKind } from "@cyborg7/observability/failure";
import { reportClientError } from "@cyborg7/observability/web";

// Each name maps to the relay KPI `client_invariant_violated{invariant}` (client-log.ts).
export type TranscriptInvariant =
  | "duplicate_prompt_bubble"
  | "unattributed_user_bubble"
  | "prompt_unconfirmed"
  | "turn_completed_without_deltas"
  | "out_of_order_insert"
  | "empty_page_refused"
  | "live_row_above_newer_prompt"
  | "recovery_stalled"
  | "turn_without_visible_progress"
  | "typing_outlived_turn"
  | "output_rewrite_unmatched";

// Each invariant's failure_kind (knowledge/123): a fault unless the UI already cleared it.
const INVARIANT_KIND: Record<TranscriptInvariant, FailureKind> = {
  duplicate_prompt_bubble: "fault",
  unattributed_user_bubble: "fault",
  prompt_unconfirmed: "fault",
  turn_completed_without_deltas: "fault",
  out_of_order_insert: "fault",
  empty_page_refused: "fault",
  live_row_above_newer_prompt: "fault",
  recovery_stalled: "fault",
  turn_without_visible_progress: "fault",
  typing_outlived_turn: "recovered",
  output_rewrite_unmatched: "fault",
};

export type AgeBucket = "lt_5m" | "lt_1h" | "lt_1d" | "older";

type Id = string | number | boolean | null | undefined;

const THROTTLE_MS = 60_000;
// Bounded: a scope (e.g. one per turn) must not grow the map without end; oldest keys go first.
export const MAX_THROTTLE_KEYS = 2_000;

export function ageBucket(ageMs: number): AgeBucket {
  if (ageMs < 5 * 60_000) return "lt_5m";
  if (ageMs < 60 * 60_000) return "lt_1h";
  if (ageMs < 24 * 60 * 60_000) return "lt_1d";
  return "older";
}

// Ids, enums and counters only, never content; one report per agent + invariant (+ `scope`,
// which is never sent) a minute. Owned by the agent store, cleared with it at sign-out.
export class TranscriptInvariantReporter {
  private readonly lastReported = new Map<string, number>();

  constructor(
    private readonly maxKeys = MAX_THROTTLE_KEYS,
    private readonly subject?: (agentId: string) => Record<string, string>,
  ) {}

  report(
    name: TranscriptInvariant,
    ids: { agentId?: string | null } & Record<string, Id> = {},
    now: number = Date.now(),
    scope = "",
    failureKind: FailureKind = INVARIANT_KIND[name],
  ): boolean {
    try {
      const key = `${ids.agentId ?? ""}\u0000${name}\u0000${scope}`;
      const last = this.lastReported.get(key);
      if (last !== undefined && now - last < THROTTLE_MS) return false;
      this.lastReported.delete(key);
      this.lastReported.set(key, now);
      if (this.lastReported.size > this.maxKeys) {
        const oldest = this.lastReported.keys().next();
        if (!oldest.done) this.lastReported.delete(oldest.value);
      }
      const fields: Record<string, Id> = {};
      for (const [field, value] of Object.entries(ids))
        if (value !== undefined) fields[field] = value;
      // The subject first: a field the caller passed (its record's workspaceId) wins.
      reportClientError({
        ...(ids.agentId ? this.subject?.(ids.agentId) : undefined),
        ...fields,
        source: `agents.invariant.${name}`,
        message: name,
        invariant: name,
        failure_kind: failureKind,
      });
      return true;
    } catch {
      // intentional: an invariant report must never become a failure in the UI
      return false;
    }
  }

  get size(): number {
    return this.lastReported.size;
  }

  clear(): void {
    this.lastReported.clear();
  }
}
